import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Button,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import axios from "axios";
import { toast } from "react-toastify";
import QrCodeUploadByDoctor from "../doctor-console/QrCodeUploadByDoctor";
import { StyledTextFieldBootstrap } from "../doctor-console/StyledComponents";
import { normalizeBase64Image } from "../utils/utils";
import {
  displayPatientName,
  displayPatientNameFromParts,
} from "../../utils/displayPatientName";
import { usePermissions } from "../login/PermissionContext";

const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL;

export default function QrDocumentUpload() {
  const { userInfo } = usePermissions();
  const [authToken] = useState(sessionStorage.getItem("accessToken") || "");
  const headers = { Authorization: `Bearer ${authToken}` };

  const [selectedPatient, setSelectedPatient] = useState(null);
  const [openPatientSearch, setOpenPatientSearch] = useState(false);
  const [patientSearchType, setPatientSearchType] = useState("abhaId");
  const [patientSearchQuery, setPatientSearchQuery] = useState("");
  const [patientSearchName, setPatientSearchName] = useState("");
  const [patientSearchGender, setPatientSearchGender] = useState("");
  const [patientSearchDob, setPatientSearchDob] = useState("");
  const [patientSearchResults, setPatientSearchResults] = useState([]);
  const [isPatientSearching, setIsPatientSearching] = useState(false);
  const [selectedPatientSearchId, setSelectedPatientSearchId] = useState("");

  const orgCode = sessionStorage.getItem("ORG_CODE") || "ORGCXYHM";
  const appMode = sessionStorage.getItem("APP_MODE") || "SaaS";
  const facilityName =
    userInfo?.facility_name ||
    sessionStorage.getItem("facility_display_name") ||
    "";

  const formatAbhaNumber = (abhaNumber) => {
    const digits = String(abhaNumber || "").replace(/\D/g, "");
    if (digits.length !== 14) return abhaNumber || "";
    return digits.replace(/(\d{2})(\d{4})(\d{4})(\d{4})/, "$1-$2-$3-$4");
  };

  const runPatientSearch = async () => {
    if (!authToken) return;

    let filterCriteria;

    if (patientSearchType === "demographics") {
      if (
        !String(patientSearchName || "").trim() &&
        !String(patientSearchGender || "").trim() &&
        !String(patientSearchDob || "").trim()
      ) {
        toast.error("Please enter at least one demographic field.");
        return;
      }
      filterCriteria = {
        filter_type: "demographics",
        filter_details: {
          name: patientSearchName || "",
          gender: patientSearchGender || "",
          dob: patientSearchDob || "",
        },
        multipleResults: true,
      };
    } else {
      const q = String(patientSearchQuery || "").trim();
      if (!q) {
        toast.error("Please enter a search query.");
        return;
      }

      if (patientSearchType === "abhaId") {
        filterCriteria = {
          filter_type: "abha_id",
          filter_details: { abha_id: q },
          multipleResults: true,
        };
      } else if (patientSearchType === "abhaNo") {
        filterCriteria = {
          filter_type: "abha_number",
          filter_details: { abha_number: formatAbhaNumber(q) },
          multipleResults: true,
        };
      } else if (patientSearchType === "phoneNo") {
        filterCriteria = {
          filter_type: "phone_number",
          filter_details: { phone_number: q },
          multipleResults: true,
        };
      } else if (patientSearchType === "patientId") {
        filterCriteria = {
          filter_type: "patient_id",
          filter_details: { patient_id: q },
          multipleResults: true,
        };
      } else {
        toast.error("Invalid search type.");
        return;
      }
    }

    try {
      setIsPatientSearching(true);
      const res = await axios.post(
        `${REACT_APP_BACKEND_ROUTE_URL}get_filtered_patient_data/`,
        filterCriteria,
        { headers }
      );

      if (res?.data?.status === "Success") {
        const found = Array.isArray(res?.data?.patient_details)
          ? res.data.patient_details
          : [];
        setPatientSearchResults(found);
        setSelectedPatientSearchId("");
        toast.success("Patient Found Successfully");
      } else {
        setPatientSearchResults([]);
        setSelectedPatientSearchId("");
        toast.error(res?.data?.message || "No patient found");
      }
    } catch (e) {
      console.error("Patient search failed:", e);
      setPatientSearchResults([]);
      setSelectedPatientSearchId("");
      toast.error("Patient search failed");
    } finally {
      setIsPatientSearching(false);
    }
  };

  const selectPatientFromSearch = (patient) => {
    if (!patient?.patient_id) return;
    setSelectedPatient(patient);
    setOpenPatientSearch(false);
  };

  const clearPatient = () => {
    setSelectedPatient(null);
    setPatientSearchResults([]);
    setSelectedPatientSearchId("");
  };

  const renderPatientCard = () => {
    if (!selectedPatient) {
      return (
        <>
          <Card
            onClick={() => setOpenPatientSearch(true)}
            sx={{
              borderRadius: "10px",
              border: "1px dashed #125386",
              boxShadow: "none",
              cursor: "pointer",
              "&:hover": { backgroundColor: "action.hover" },
            }}
          >
            <CardContent sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <Avatar sx={{ bgcolor: "#213360", width: 56, height: 56 }}>P</Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700 }}>No patient selected</Typography>
                <Typography variant="body2" sx={{ color: "#6B7A90" }}>
                  Search by Patient ID / ABHA / Phone / Demographics
                </Typography>
              </Box>
            </CardContent>
          </Card>
          <Button
            type="button"
            variant="text"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setOpenPatientSearch(true);
            }}
            sx={{ textTransform: "none", pl: 0 }}
          >
            Search patient
          </Button>
        </>
      );
    }

    const photoSrc = normalizeBase64Image(selectedPatient.photo);
    const gender = selectedPatient.gender || "-";
    const dob = selectedPatient.dob || "-";
    const age =
      selectedPatient.age !== undefined && selectedPatient.age !== null
        ? `${selectedPatient.age}`
        : "-";
    const phone =
      selectedPatient.phone_no || selectedPatient.phone_number || "-";

    return (
      <>
        <Card
          onClick={() => setOpenPatientSearch(true)}
          sx={{
            borderRadius: "10px",
            border: "1px solid #125386",
            boxShadow: "none",
            cursor: "pointer",
            "&:hover": { backgroundColor: "action.hover" },
          }}
        >
          <CardContent sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Avatar
              src={photoSrc || undefined}
              alt={displayPatientName(selectedPatient.name, "Patient")}
              sx={{ width: 56, height: 56, bgcolor: "#213360" }}
            >
              {String(selectedPatient?.name || "P")
                .slice(0, 1)
                .toUpperCase()}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 800, textTransform: "uppercase" }}>
                {displayPatientName(selectedPatient.name, "-")}
              </Typography>
              <Typography variant="body2">
                Patient ID: {selectedPatient.patient_id || "-"}
              </Typography>
              <Typography variant="body2">
                Gender: {gender} &nbsp;|&nbsp; DOB: {dob} &nbsp;|&nbsp; Age:{" "}
                {age}
              </Typography>
              <Typography variant="body2">Phone: {phone}</Typography>
            </Box>
          </CardContent>
        </Card>
        <Box sx={{ display: "flex", gap: 1 }} onClick={(e) => e.stopPropagation()}>
          <Button
            type="button"
            variant="text"
            size="small"
            onClick={() => setOpenPatientSearch(true)}
            sx={{ textTransform: "none", pl: 0 }}
          >
            Change patient
          </Button>
          <Button
            type="button"
            variant="text"
            size="small"
            onClick={clearPatient}
            sx={{ textTransform: "none" }}
          >
            Clear
          </Button>
        </Box>
      </>
    );
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, maxWidth: 1100, margin: "auto" }}>
      <Paper
        elevation={2}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: "16px",
          background: "linear-gradient(135deg, #f5f7fa 0%, #e4ecf7 100%)",
        }}
      >
        <Typography
          variant="h5"
          sx={{ color: "#125386", fontWeight: 700, mb: 2 }}
        >
          QR Document Upload
        </Typography>
        {renderPatientCard()}
      </Paper>

      {selectedPatient ? (
        <Paper
          elevation={2}
          sx={{
            p: 3,
            borderRadius: "16px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{ color: "#125386", fontWeight: 600, textAlign: "center" }}
          >
            {displayPatientNameFromParts(selectedPatient, "Patient")} — Scan to
            upload documents
          </Typography>
          <Typography sx={{ color: "#666", fontSize: "0.9rem" }}>
            ID: {selectedPatient.patient_id}
          </Typography>
          <QrCodeUploadByDoctor
            patientId={selectedPatient.patient_id}
            orgCode={orgCode}
            appMode={appMode}
            facilityName={facilityName}
          />
          <Typography
            variant="body2"
            sx={{ color: "#666", textAlign: "center", maxWidth: 360 }}
          >
            Scan this QR code with a mobile device to open the secure upload
            page for this patient.
          </Typography>
        </Paper>
      ) : (
        <Paper
          elevation={1}
          sx={{
            p: 6,
            borderRadius: "16px",
            textAlign: "center",
            minHeight: "40vh",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            background: "#fafbfc",
          }}
        >
          <QrCode2Icon sx={{ fontSize: 80, color: "#ccc", mb: 2 }} />
          <Typography
            variant="h6"
            sx={{ color: "#888", fontWeight: 500, mb: 1 }}
          >
            Select a patient to generate a QR upload code
          </Typography>
          <Typography sx={{ color: "#aaa", fontSize: "0.9rem" }}>
            Use Search patient above — same options as invoice creation (Patient
            ID, ABHA, Phone, Demographics)
          </Typography>
        </Paper>
      )}

      <Dialog
        open={openPatientSearch}
        onClose={() => setOpenPatientSearch(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle
          sx={{
            backgroundColor: "#213360",
            color: "#FFFFFF",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span>Search Patient</span>
          <IconButton onClick={() => setOpenPatientSearch(false)}>
            <span style={{ color: "#FFFFFF", fontSize: 22 }}>×</span>
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <label className="required-label">Search By</label>
              <StyledTextFieldBootstrap
                fullWidth
                select
                value={patientSearchType}
                onChange={(e) => {
                  setPatientSearchType(e.target.value);
                  setPatientSearchResults([]);
                  setSelectedPatientSearchId("");
                }}
              >
                <MenuItem value="patientId">Patient ID</MenuItem>
                <MenuItem value="abhaId">ABHA Address</MenuItem>
                <MenuItem value="abhaNo">ABHA No</MenuItem>
                <MenuItem value="phoneNo">Phone No</MenuItem>
                <MenuItem value="demographics">Demographics</MenuItem>
              </StyledTextFieldBootstrap>
            </Grid>

            <Grid item xs={12} sm={6}>
              <label>Enter Search Query</label>
              <StyledTextFieldBootstrap
                fullWidth
                placeholder="Enter your search query"
                value={patientSearchQuery}
                onChange={(e) => setPatientSearchQuery(e.target.value)}
                disabled={patientSearchType === "demographics"}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <label>Name</label>
              <StyledTextFieldBootstrap
                fullWidth
                placeholder="Enter patient name"
                value={patientSearchName}
                onChange={(e) => setPatientSearchName(e.target.value)}
                disabled={patientSearchType !== "demographics"}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <label>Gender</label>
              <StyledTextFieldBootstrap
                fullWidth
                select
                value={patientSearchGender}
                onChange={(e) => setPatientSearchGender(e.target.value)}
                disabled={patientSearchType !== "demographics"}
              >
                <MenuItem value="">Select Gender</MenuItem>
                <MenuItem value="M">Male</MenuItem>
                <MenuItem value="F">Female</MenuItem>
                <MenuItem value="O">Other</MenuItem>
              </StyledTextFieldBootstrap>
            </Grid>

            <Grid item xs={12} sm={4}>
              <label>Date of Birth</label>
              <StyledTextFieldBootstrap
                fullWidth
                type="date"
                value={patientSearchDob}
                onChange={(e) => setPatientSearchDob(e.target.value)}
                disabled={patientSearchType !== "demographics"}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Results
            </Typography>

            {isPatientSearching ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                <CircularProgress size={20} />
                <span>Searching…</span>
              </Box>
            ) : patientSearchResults.length > 0 ? (
              <Grid container spacing={2}>
                {patientSearchResults.map((p) => {
                  const isSelected = selectedPatientSearchId === p.patient_id;
                  const phoneVal = p.phone_no || p.phone_number || "-";
                  return (
                    <Grid item xs={12} sm={6} key={p.patient_id}>
                      <Card
                        onClick={() => {
                          setSelectedPatientSearchId(p.patient_id);
                          selectPatientFromSearch(p);
                        }}
                        sx={{
                          cursor: "pointer",
                          border: isSelected
                            ? "2px solid #21cdc0"
                            : "1px solid #E3EFF0",
                          borderRadius: "10px",
                        }}
                      >
                        <CardContent sx={{ display: "flex", gap: 2 }}>
                          <Avatar
                            src={normalizeBase64Image(p?.photo) || undefined}
                            alt={p?.name || "Patient"}
                            sx={{ bgcolor: "#213360" }}
                          >
                            {String(p?.name || "P")
                              .slice(0, 1)
                              .toUpperCase()}
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 700 }}>
                              {p?.name || "-"}
                            </Typography>
                            <Typography variant="body2">
                              Patient ID: {p?.patient_id || "-"}
                            </Typography>
                            <Typography variant="body2">
                              Phone: {phoneVal}
                            </Typography>
                            <Typography variant="body2">
                              ABHA Number: {p?.abha_no || "-"}
                            </Typography>
                            <Typography variant="body2">
                              ABHA Address: {p?.abha_id || "-"}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            ) : (
              <Typography variant="body2" sx={{ color: "#6B7A90" }}>
                No results to show.
              </Typography>
            )}
          </Box>
        </DialogContent>

        <Box
          sx={{
            p: "16px 24px",
            display: "flex",
            gap: "10px",
            width: "100%",
          }}
        >
          <button
            type="button"
            className="btn btn__secondary btn__block"
            onClick={() => setOpenPatientSearch(false)}
          >
            CLOSE
          </button>
          <button
            type="button"
            className="btn btn__secondary btn__block"
            onClick={runPatientSearch}
            disabled={isPatientSearching}
          >
            {isPatientSearching ? "SEARCHING..." : "SEARCH"}
          </button>
        </Box>
      </Dialog>
    </Box>
  );
}
